/* eslint-disable no-lone-blocks */
/* eslint-disable no-nested-ternary */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Section } from '@heronhr/trivia-games';
import { BillingTooltip } from 'components/Tooltip';
import { ReactComponent as InfoIcon } from 'assets/images/home/infoIcon.svg';
import Button from 'components/Button';
import { useSelector } from 'react-redux';
import moment from 'moment';
import StandardProSection from './StandardProOverviewSection';
import BasicOverViewSection from './BasicOverViewSection';
import styles from './styles.module.scss';

const OverviewSection = ({
  platform,
  planResponse,
  userData,
  channels,
  uniqueUsersCount,
  selectedChannels,
  activeUsers,
  setSelectedChannels,
  dropDownChannelOptions,
  participantLeftOut,
  teamsSubscription,
  handleBuySubscription,
  openCustomerPortal,
  channelBasedBilling,
  handleButtonClick,
  unassignedUsers
}) => {
  const { plan, planPrices } = planResponse;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const { freeTrial, usersCount } = userData;
  const user = useSelector((state) => state.user);
  const { trialExpiresAt } = user;
  const trialDaysLeft = moment(trialExpiresAt).diff(moment(), 'days', false);
  const restrictedPlayers = participantLeftOut?.length;
  const noRestrictedPlayersInBasicPlan = restrictedPlayers < 1 && plan === 'BASIC';
  const handleClickInChannelDropdown = (selectedChannel) => {
    setSelectedChannels(selectedChannel);
    setHasChanges(true);
  };
  const handleClick = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleUpgradeClick = () => {
    if (platform === 'teams') {
      window.open(`${process.env.REACT_APP_TEAMS_APP_SOURCE_URL}`);
    } else {
      const comparePlanSection = document.getElementById('compare-plans-section');
      if (comparePlanSection) {
        comparePlanSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <Section>
      {freeTrial || noRestrictedPlayersInBasicPlan ? (
        <div className={styles.freeTrailWrapper}>
          <div>Overview</div>
          <div className={styles.freeTrailBody}>
            <div className={styles.planDetails}>
              <div className={styles.planInfo}>
                Free plan{noRestrictedPlayersInBasicPlan ? `- Basic` : null}
                <span>
                  {noRestrictedPlayersInBasicPlan ? `(${planPrices?.TERM || 'month'}ly)` : `(Pro)`}
                </span>
                <div className={styles.currentPlan}>Current Plan</div>
              </div>
              {noRestrictedPlayersInBasicPlan ? (
                <div>
                  <div>
                    {activeUsers}
                    <span>
                      {' '}
                      <BillingTooltip title='Any user who plays a Game or uses other Trivia features are called Trivia Users.'>
                        <InfoIcon />
                      </BillingTooltip>
                    </span>
                  </div>
                  <div className={styles.currentPlan}>Active users</div>
                </div>
              ) : (
                <div>
                  <div>
                    {trialDaysLeft >= 0 ? trialDaysLeft : 'no'} days left
                    <span>
                      {' '}
                      <BillingTooltip
                        title={`${trialDaysLeft} days left for your Free plan to complete. Upgrade now to continue playing with Trivia.`}
                      >
                        <InfoIcon />
                      </BillingTooltip>
                    </span>
                  </div>
                  <div className={styles.currentPlan}>of Free Trail</div>
                </div>
              )}
            </div>
            <div className={styles.OverviewButton}>
              <Button onClick={handleUpgradeClick} className={styles.overviewUpgradeplanButton}>
                Upgrade now
              </Button>
              <div className={styles.currentPlan}>
                and increase your engagement rate to at least 50%
              </div>
            </div>
          </div>
        </div>
      ) : plan === 'BASIC' || platform === 'teams' ? (
        <BasicOverViewSection
          plan={plan}
          handleBuySubscription={handleBuySubscription}
          platform={platform}
          planResponse={planResponse}
          teamsSubscription={teamsSubscription}
          handleClick={handleClick}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          openCustomerPortal={openCustomerPortal}
          participantLeftOut={participantLeftOut}
          unassignedUsers={unassignedUsers}
        />
      ) : (
        plan !== null && (
          <StandardProSection
            plan={plan}
            term={planResponse.planPrices?.TERM || 'month'}
            channelBasedBilling={channelBasedBilling}
            handleClickInChannelDropdown={handleClickInChannelDropdown}
            usersCount={usersCount}
            handleClick={handleClick}
            dropDownChannelOptions={dropDownChannelOptions}
            uniqueUsersCount={uniqueUsersCount}
            channels={channels}
            isModalOpen={isModalOpen}
            hasChanges={hasChanges}
            selectedChannels={selectedChannels}
            setSelectedChannels={setSelectedChannels}
            handleButtonClick={handleButtonClick}
          />
        )
      )}
    </Section>
  );
};

OverviewSection.propTypes = {
  platform: PropTypes.string.isRequired,
  planResponse: PropTypes.shape({
    plan: PropTypes.string.isRequired,
    channelBasedBilling: PropTypes.bool.isRequired,
    planPrices: PropTypes.shape({
      STANDARD: PropTypes.number.isRequired,
      PRO: PropTypes.number.isRequired,
      TERM: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  userData: PropTypes.shape({
    freeTrial: PropTypes.bool.isRequired,
    usersCount: PropTypes.number.isRequired
  }).isRequired,
  channels: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ).isRequired,
  activeUsers: PropTypes.number.isRequired,
  uniqueUsersCount: PropTypes.number.isRequired,
  selectedChannels: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedChannels: PropTypes.func.isRequired,
  dropDownChannelOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ).isRequired,
  teamsSubscription: PropTypes.shape({}).isRequired,
  channelBasedBilling: PropTypes.bool.isRequired,
  handleBuySubscription: PropTypes.func.isRequired,
  participantLeftOut: PropTypes.arrayOf(PropTypes.string).isRequired,
  openCustomerPortal: PropTypes.func.isRequired,
  handleButtonClick: PropTypes.func.isRequired,
  unassignedUsers: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired
    })
  ).isRequired
};

export default OverviewSection;
